import {
  queryOptions,
  useInfiniteQuery,
  useQueries,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { useCallback } from "react";
import {
  calculateSIP,
  fetchFundFiltersMetadata,
  fetchFundOverlapMatrix,
  fetchFundRanks,
  fetchNFOs,
  getFund,
  getFundDefferedLoadsManager,
  getFundFilters,
  getFundHoldings,
  getFundPerformance,
  getFundPortfolio,
  getFunds,
  getHistoricalNav,
} from "../../api/funds/fund";
import { CustomQueryOptions, Fund } from "../../types";

export const fundsQueryOptions = (
  variables: {
    pageNo: number;
    pageSize: number;
    search?: string;
    filters?: Record<string, string[] | string>;
    sort?: { desc: boolean; id: string }[];
    accumulatedDistributionStatus?: boolean;
  },
  { enabled = true }: CustomQueryOptions<typeof getFunds> = {}
) => {
  const {
    pageNo,
    pageSize,
    search = "",
    filters = {},
    sort,
    accumulatedDistributionStatus,
  } = variables;
  return queryOptions({
    queryKey: [
      "funds",
      {
        pageNo,
        pageSize,
        search,
        filters,
        sort,
      },
    ],
    queryFn: () =>
      getFunds(
        pageNo,
        pageSize,
        search,
        filters,
        sort,
        accumulatedDistributionStatus
      ),
    placeholderData: (prev) => prev,
    enabled,
  });
};

export function useInfiniteFunds(
  variables: {
    pageNo: number;
    pageSize: number;
    search?: string;
    filters?: Record<string, string[] | string>;
    sort?: { desc: boolean; id: string }[];
    accumulatedDistributionStatus?: boolean;
  },
  {
    maxPages = 3,
    enabled = true,
  }: { maxPages?: number; enabled?: boolean } = {}
) {
  const {
    pageSize,
    search = "",
    filters = {},
    sort,
    accumulatedDistributionStatus,
  } = variables;
  return useInfiniteQuery({
    queryKey: ["infinite-funds", { search, filters, sort }],
    queryFn: ({ pageParam = 1 }) =>
      getFunds(
        pageParam,
        pageSize,
        search,
        filters,
        sort,
        accumulatedDistributionStatus
      ),
    getNextPageParam: (lastPage, _allPages, lastPageParam) =>
      lastPage.next ? lastPageParam + 1 : undefined,
    getPreviousPageParam: (lastPage, _allPages, lastPageParam) =>
      lastPage.previous ? lastPageParam - 1 : undefined,
    initialPageParam: 1,
    enabled,
    ...(maxPages > 0 ? { maxPages } : {}),
  });
}

export function useFunds(
  variables: {
    pageNo: number;
    pageSize: number;
    search?: string;
    filters?: Record<string, string[] | string>;
    sort?: { desc: boolean; id: string }[];
    accumulatedDistributionStatus?: boolean;
  },
  options?: CustomQueryOptions<typeof getFunds>
) {
  return useQuery(fundsQueryOptions(variables, options));
}

export const fundQueryOptions = (fundId: string, initialData?: Fund) =>
  queryOptions({
    queryKey: ["fund", { fundId }],
    queryFn: () => getFund(fundId),
    initialData,
    staleTime: 5 * 60 * 1000,
  });

export function useFund(fundId: string, initialData: Fund) {
  return useQuery(fundQueryOptions(fundId, initialData));
}

export const fundPerformanceQueryOptions = (fundId: string) =>
  queryOptions({
    queryKey: ["fund-performance", { fundId }],
    queryFn: () => getFundPerformance(fundId),
  });

export const useFundPerformance = (fundId: string) =>
  useQuery(fundPerformanceQueryOptions(fundId));

export const fundPortfolioQueryOptions = (fundId: string) =>
  queryOptions({
    queryKey: ["fund-portfolio", { fundId }],
    queryFn: () => getFundPortfolio(fundId),
  });

export const useFundPortfolio = (mstarId: string) =>
  useQuery(fundPortfolioQueryOptions(mstarId));

export const fundHoldingsQueryOptions = (fundId: string) =>
  queryOptions({
    queryKey: ["fund-holdings", { fundId }],
    queryFn: () => getFundHoldings(fundId),
  });

export const useFundHoldings = (fundId: string) => {
  return useQuery(fundHoldingsQueryOptions(fundId));
};

export const defferedLoadsManagerQueryOptions = (fundId: string) =>
  queryOptions({
    queryKey: ["deffered-loads-manager", { fundId }],
    queryFn: () => getFundDefferedLoadsManager(fundId),
  });

export const useDefferedLoads = (fundId: string) =>
  useQuery(defferedLoadsManagerQueryOptions(fundId));

export const historicalNavQueryOptions = (variables: {
  fundId: string;
  startDate: string;
  endDate: string;
}) =>
  queryOptions({
    queryKey: [
      "historical-nav",
      variables.fundId,
      { startDate: variables.startDate, endDate: variables.endDate },
    ],
    queryFn: () =>
      getHistoricalNav(
        variables.fundId,
        variables.startDate,
        variables.endDate
      ),
    enabled: !!variables.fundId,
    staleTime: 5 * 60 * 1000,
    placeholderData: (d) => d,
  });

export const useHistoricalNav = (variables: {
  fundId: string;
  startDate: string;
  endDate: string;
}) => useQuery(historicalNavQueryOptions(variables));

export const fundFiltersQueryOptions = (fieldName: string) =>
  queryOptions({
    queryKey: ["fund-filters", { fieldName }],
    queryFn: () => getFundFilters(fieldName),
  });

export const useFundFilters = (fieldName: string) =>
  useQuery(fundFiltersQueryOptions(fieldName));

export const useCompareFunds = (fundIds: string[]) => {
  const queries = useQueries({
    queries: fundIds.map((fundId) => fundQueryOptions(fundId)),
    combine: (results) => {
      return {
        data: results.map((result) => result.data),
        isLoading: results.some((result) => result.isLoading),
        isError: results.some((result) => result.isError),
      };
    },
  });

  return queries;
};

export const useCompareFundsDetailedPerformance = (fundIds: string[]) => {
  return useQueries({
    queries: fundIds.map((fundId) => fundPerformanceQueryOptions(fundId)),
    combine: (results) => {
      return {
        data: results.map((result) => result.data),
        isLoading: results.some((result) => result.isLoading),
        isError: results.some((result) => result.isError),
      };
    },
  });
};

export const useCompareFundsDefferedLoads = (fundIds: string[]) => {
  return useQueries({
    queries: fundIds.map((fundId) => defferedLoadsManagerQueryOptions(fundId)),
    combine: (results) => {
      return {
        data: results.map((result) => result.data),
        isLoading: results.some((result) => result.isLoading),
        isError: results.some((result) => result.isError),
      };
    },
  });
};

export const useCompareFundsHistoricalNav = (
  fundIds: string[],
  startDate: string,
  endDate: string
) => {
  return useQueries({
    queries: fundIds.map((fundId) =>
      historicalNavQueryOptions({ fundId, startDate, endDate })
    ),
    combine: (results) => {
      return {
        data: results.map((result) =>
          result.data?.reduce(
            (acc, item) => {
              acc[item.date] = parseFloat(item.nav.toString());
              return acc;
            },
            {} as Record<string, number>
          )
        ),
        isLoading: results.some((result) => result.isLoading),
        isError: results.some((result) => result.isError),
      };
    },
  });
};

export const useCompareFundPerformances = (fundIds: string[]) => {
  return useQueries({
    queries: fundIds.map((fundId) => fundPerformanceQueryOptions(fundId)),
    combine: (results) => {
      return {
        data: results.map((result) => result.data),
        isLoading: results.some((result) => result.isLoading),
        isError: results.some((result) => result.isError),
      };
    },
  });
};

export const useCompareFundPortfolios = (fundIds: string[]) => {
  return useQueries({
    queries: fundIds.map((fundId) => fundPortfolioQueryOptions(fundId)),
    combine: (results) => {
      return {
        data: results.map((result) => result.data),
        isLoading: results.some((result) => result.isLoading),
        isError: results.some((result) => result.isError),
      };
    },
  });
};

export const sipCalculatorQueryOptions = (
  variables: {
    fundId: string;
    startDate: string;
    endDate: string;
    sipFrequency: string;
    sipAmount?: number;
    lumpsumAmount?: number;
  },
  options?: CustomQueryOptions<typeof calculateSIP>
) => {
  const { fundId, startDate, endDate, sipFrequency, sipAmount, lumpsumAmount } =
    variables;
  return queryOptions({
    queryKey: [
      "sip-calculator",
      { fundId, startDate, endDate, sipFrequency, sipAmount, lumpsumAmount },
    ],
    queryFn: () =>
      calculateSIP({
        fund_id: fundId,
        start_date: startDate,
        end_date: endDate,
        sip_frequency: sipFrequency,
        sip_amount: sipAmount,
        lumpsum_amount: lumpsumAmount,
      }),
    staleTime: 5 * 60 * 1000,
    ...options,
  });
};

export const useSIPCalculator = (
  variables: {
    fundIds: string[];
    startDate: string;
    endDate: string;
    sipFrequency: string;
    sipAmount?: number;
    lumpsumAmount?: number;
  },
  options?: CustomQueryOptions<typeof calculateSIP>
) => {
  return useQueries({
    queries: variables.fundIds.map((fundId) =>
      sipCalculatorQueryOptions({ ...variables, fundId }, options)
    ),
    combine: (results) => {
      return {
        data: results.map((result) => ({
          ...result.data,
          data: result?.data?.data?.reduce(
            (acc, i) => {
              acc[i.date] = {
                date: i.date,
                total_invested: i.total_invested,
                portfolio_value: i.portfolio_value,
              };
              return acc;
            },
            {} as Record<
              string,
              { date: string; total_invested: number; portfolio_value: number }
            >
          ),
        })),
        isLoading: results.some((result) => result.isLoading),
        isError: results.some((result) => result.isError),
      };
    },
  });
};

export function useNFOs() {
  return useQuery({
    queryKey: ["nfo"],
    queryFn: fetchNFOs,
  });
}

export function useFundFiltersMetadata(
  fields: string[],
  options?: { enabled?: boolean }
) {
  return useQuery({
    queryKey: ["fund_filters_metadata", fields],
    queryFn: () => fetchFundFiltersMetadata(fields),
    staleTime: Infinity,
    enabled: options?.enabled,
  });
}

export function useFundRanks(
  variables: {
    page?: number;
    pageSize?: number;
    filters: Record<string, string>;
  },
  options?: { enabled: boolean }
) {
  return useQuery({
    queryKey: ["fund_ranks", { ...variables }],
    queryFn: () => fetchFundRanks(variables),
    ...options,
  });
}

export function useFetchFundByIsin() {
  const queryClient = useQueryClient();

  return useCallback(
    async (isin: string) => {
      const data = await queryClient.ensureQueryData(
        fundsQueryOptions({
          pageNo: 1,
          pageSize: 1,
          filters: { isin },
        })
      );
      return data.results?.[0];
    },
    [queryClient]
  );
}

export function useFundOverlapMatrix(funds: string[]) {
  return useQuery({
    queryKey: ["fund-overlap-matrix", { funds }],
    queryFn: () => fetchFundOverlapMatrix(funds),
  });
}
